import { type Option } from "@tento-lona/sheets";
import { RowLocalId } from "@tento-lona/sheets";
import type { RowSyncState } from "@tento-lona/sheets";
import { SheetViewModel } from "@tento-lona";
import { DateTime, NaiveDate, Utc } from "@tento-chrono";
import { nonnull } from "@tento-core/assert";
import { LiveData } from "@tento-core/livedata";
import { optionLiveData } from "./option-live-data.ts";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { SheetLayout, SheetLayoutPort, SheetScrollPort } from "@tento-ui/components/sheet/sheet-layout";
import type { SheetDelegate } from "./sheet-delegate.ts";
import { SizeGate } from "@tento-ui/ui/size-gate";
import { SheetBinder } from "./sheet-index-renderer.ts";

/**
 * The sheet element's state handle (F11): what renderers read of the bound
 * sheet, its widths, visible/rendered columns, clock, column geometry and
 * pointer. Moved out of sheet-element.ts unchanged; the element hands in its
 * state as getters read when a member is read.
 */

/** What the handle reads from its sheet element (read when a method runs). */
export type StateHandleHost = {
  /** The bound sheet, if any. */
  readonly sheet: Option<SheetViewModel.Surface<SheetBinder>>;
  /** The resolved cell width. */
  readonly resolvedCellWidth: LiveData.Readable<Option<SheetLayout.Width>>;
  /** The resolved cell width in pixels. */
  readonly resolvedCellWidthPx: Option<number>;
  /** The element's measured size. */
  readonly size: SizeGate["currentSize"];
  /** Width occluded on the left. */
  readonly leftOccludedWidthPx: number;
  /** Width occluded on the right. */
  readonly rightOccludedWidthPx: number;
  /** The layout's column surface. */
  readonly columns: SheetLayoutPort["columns"];
  /** The sheet's clock. */
  readonly currentTime: LiveData.Readable<DateTime<Utc>>;
  /** The layout's scroll port. */
  readonly scrollPort: SheetScrollPort;
  /** The row sync-state provider, if any. */
  readonly syncStateProvider: Option<(rowId: RowLocalId) => Option<RowSyncState>>;
};

/** The handle the element exposes as `stateHandle`. */
export function makeStateHandle(host: StateHandleHost): SheetDelegate.State {
  const handle: SheetDelegate.State = {
      get sheet(): SheetViewModel.Surface<SheetBinder> {
        return nonnull(host.sheet, "Sheet: no sheet bound");
      },
      get resolvedWidth(): SheetLayout.Width {
        return nonnull(host.resolvedCellWidth.get());
      },
      get resolvedWidthPx(): number {
        return nonnull(host.resolvedCellWidthPx);
      },
      get visibleWidthPx(): Option<number> {
        const size = host.size;
        if (!size) return null;
        return (
          size.width - host.leftOccludedWidthPx - host.rightOccludedWidthPx
        );
      },
      get visibleColumns(): Option<ColumnIndex.Key.Resolved.Default.Range> {
        return host.columns.visible ?? null;
      },
      get visibleColumnCount(): number {
        const visible = host.columns.visible;
        if (!visible) return 0;
        return visible.end.offset - visible.start.offset + 1;
      },
      get renderedColumns(): Option<ColumnIndex.Key.Resolved.Default.Range> {
        return host.columns.rendered ?? null;
      },
      get currentTime(): LiveData.Readable<DateTime<Utc>> {
        return host.currentTime;
      },
      getColumnWidthPx(index: number): number {
        return host.scrollPort.cellWidthAt(index);
      },
      getColumnOffsetPx(index: number): number {
        return host.scrollPort.offsetOf(index) ?? 0;
      },
      getColumnRangeWidthPx(startOffset: number, endOffset: number): number {
        const port = host.scrollPort;
        const startPx = port.offsetOf(startOffset);
        const endPx = port.offsetOf(endOffset + 1);
        if (startPx == null || endPx == null) {
          // Fallback: sum individual column widths
          let width = 0;
          for (let i = startOffset; i <= endOffset; i++) {
            width += handle.getColumnWidthPx(i);
          }
          return width;
        }
        return endPx - startPx;
      },
      dateForOffset(anchorDate: NaiveDate.Partial, offsetX: number): NaiveDate {
        const mapper = nonnull(host.sheet).indexRenderer.mapper;
        const anchor = anchorDate.toRange().start;
        const anchorKey = ColumnIndex.Key.fromDate(anchor, "lower");
        const anchorIdx = mapper.indexOfKey(anchorKey);
        const anchorOffsetPx = this.getColumnOffsetPx(anchorIdx.index);
        const targetOffsetPx = anchorOffsetPx + offsetX;

        let idx = anchorIdx.index;
        let currentOffset = anchorOffsetPx;

        if (targetOffsetPx >= currentOffset) {
          // Walk forward
          while (true) {
            const columnWidth = this.getColumnWidthPx(idx);
            const nextOffset = currentOffset + columnWidth;
            if (targetOffsetPx < nextOffset) {
              break;
            }
            currentOffset = nextOffset;
            idx++;
          }
        } else {
          // Walk backward
          while (idx > 0 && targetOffsetPx < currentOffset) {
            idx--;
            currentOffset = this.getColumnOffsetPx(idx);
          }
        }

        // Resolve the target column to get its start date
        const targetKey = ColumnIndex.Key.fromIndex(idx);
        const targetResolved = mapper.resolveKeyToDefault(targetKey, "lower");
        const [targetPartial] = targetResolved.discrete();
        const targetRange = targetPartial.toRange();
        const colStart = targetRange.start;

        // Compute sub-column day offset (for week/month timescales)
        const colWidthPx = this.getColumnWidthPx(idx);
        // end is exclusive (week: Mon + 7 days = next Mon), so no + 1
        const daysInColumn = targetRange.end.differenceInDays(colStart);
        if (daysInColumn <= 1 || colWidthPx <= 0) return colStart;

        const fractionWithinCol = (targetOffsetPx - currentOffset) / colWidthPx;
        const subDayOffset = Math.max(
          0,
          Math.min(
            Math.floor(fractionWithinCol * daysInColumn),
            daysInColumn - 1,
          ),
        );

        return colStart.addDays(subDayOffset);
      },
      dateForContentX(contentX: number): Option<NaiveDate> {
        // Use the first visible column as a stable anchor, then compute
        // the date from the pixel offset using dateForOffset.
        const visibleCols = this.visibleColumns;
        if (!visibleCols) return null;

        const anchorOffset = visibleCols.start.offset;
        const anchorPx = this.getColumnOffsetPx(anchorOffset);
        const anchorDate = visibleCols.start.discrete()[0];

        return this.dateForOffset(anchorDate, contentX - anchorPx);
      },
      get peekSyncState(): Option<(rowId: RowLocalId) => Option<RowSyncState>> {
        return host.syncStateProvider;
      },
      get pointerPosition() {
        return optionLiveData(host.scrollPort.pointerPosition);
      },
  };
  return handle;
}
